import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, X, Edit, Plus } from 'lucide-react';
import React from 'react';

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const DayEventsModal = ({
  isOpen,
  onClose,
  date,
  events,
  employees,
  onEditEvent,
  onCreateEvent
}) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEmployeeNames = (employeeIds) => {
    return employeeIds.map(id => {
      const employee = employees.find(e => e.id === id);
      return employee ? employee.name : 'Unknown';
    }).join(', ');
  };

  const getEventColor = (event) => {
    const colors = {
      primary: 'bg-primary-100 border-primary-200 text-primary-800',
      secondary: 'bg-secondary-100 border-secondary-200 text-secondary-800',
      accent: 'bg-accent-100 border-accent-200 text-accent-800',
      warning: 'bg-warning-100 border-warning-200 text-warning-800',
      success: 'bg-success-100 border-success-200 text-success-800'
    };
    return colors[event.color] || colors.primary;
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      meeting: Users,
      task: Calendar,
      reminder: Clock,
      event: Calendar
    };
    return icons[type] || Calendar;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Overlay */}
        <motion.div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-white rounded-xl shadow-intense max-w-2xl w-full max-h-[80vh] overflow-hidden"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="subsection-title">Events for {formatDate(date)}</h2>
              <p className="text-gray-600 mt-1">{events.length} event{events.length !== 1 ? 's' : ''} scheduled</p>
            </div>
            <div className="flex items-center gap-3">
              <motion.button
                className="btn-primary flex items-center"
                onClick={() => onCreateEvent(date)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus size={16} className="mr-2" />
                Create Event
              </motion.button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
            {events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No events scheduled</h3>
                <p className="text-gray-600 mb-4">This day is free. Would you like to create an event?</p>
                <motion.button
                  className="btn-primary"
                  onClick={() => onCreateEvent(date)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Create First Event
                </motion.button>
              </div>
            ) : (
              <div className="space-y-4">
                {events
                  .sort((a, b) => a.time.localeCompare(b.time))
                  .map((event) => {
                    const EventTypeIcon = getEventTypeIcon(event.type);
                    const colorClasses = getEventColor(event);

                    return (
                      <motion.div
                        key={event.id}
                        className={`p-4 rounded-lg border ${colorClasses} cursor-pointer hover:shadow-md transition-all`}
                        onClick={() => onEditEvent(event)}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`p-2 rounded-lg ${colorClasses.replace('text-', 'bg-').replace('-800', '-200').replace('-100', '-600').replace('text-', 'text-')}`}>
                              <EventTypeIcon size={16} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900">{event.title}</h4>
                                <div className="flex items-center text-sm text-gray-600">
                                  <Clock size={14} className="mr-1" />
                                  <span>{event.time} - {event.endTime}</span>
                                </div>
                              </div>

                              {event.description && (
                                <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                              )}

                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  {event.location && (
                                    <div className="flex items-center">
                                      <MapPin size={14} className="mr-1" />
                                      <span>{event.location}</span>
                                    </div>
                                  )}
                                  <span className="capitalize">{event.type}</span>
                                </div>

                                {event.taggedEmployees.length > 0 && (
                                  <div className="flex items-center">
                                    <Users size={14} className="mr-1" />
                                    <span className="text-sm text-gray-600">
                                      {getEmployeeNames(event.taggedEmployees)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <motion.button
                            className="p-2 hover:bg-gray-100 rounded-lg ml-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditEvent(event);
                            }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit size={16} className="text-gray-600" />
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DayEventsModal;















